import { OrderOperationType } from 'api/types/entity';
import { OrderExecutionTermsOrderType } from 'api/types/entity/order/orderExecutionTermsEntity';
import { CreateOrderParams } from 'api/types/params';
import { lodash } from 'helpers';
import { DEFAULT_NOMINAL_VALUE } from 'modules/order/common/constants';
import { OrderFormType } from 'modules/order/common/forms/orderForm';
import { OrderDetailsFormType } from 'modules/order/common/forms/type';

const parseDetails = (details: OrderDetailsFormType) => ({
  accountNumber: details.accountNumber,
  brokerName: {
    referenceRecordId:
      details.brokerName.value !== details.brokerName.label
        ? details.brokerName.value
        : undefined,
    value: details.brokerName.label,
  },
  custodianOrganization: {
    referenceRecordId:
      details.custodianOrganization.value !==
      details.custodianOrganization.label
        ? details.custodianOrganization.value
        : undefined,
    value: details.custodianOrganization.label,
  },
  custodianOrganizationCode: details.custodianOrganizationCode ?? undefined,
  accountOwnerName: details.accountOwnerName,
});

export const getOrderSubmitData = (
  orderType: OrderOperationType,
  values: OrderFormType
): CreateOrderParams => {
  const isWithdrawalOrDeposit =
    orderType === OrderOperationType.SecuritiesWithdrawal ||
    orderType === OrderOperationType.SecuritiesDeposit;

  return {
    ownerId: values.client?.value!,
    ownerType: values.clientType?.value!,
    executionPeriodInBusinessDays: values.executionPeriodInBusinessDays,
    executionTerms: {
      additionalNotes: values.executionTerms?.additionalNotes ?? undefined,
      orderType: values.executionTerms?.orderType?.value ?? undefined,
      yield: values.executionTerms?.yield ?? undefined,
      operationKind: values.executionTerms?.operationKind?.value ?? undefined,
      operationPurpose: values.executionTerms?.operationPurpose ?? undefined,
    },
    endDate: values.endDate!,
    financialInstrument: {
      issuer: values.financialInstrument?.issuer ?? undefined,
      isin: {
        value: values.financialInstrument?.isin.label,
        referenceRecordId: values.financialInstrument?.isin.record?.id
          ? values.financialInstrument?.isin.value
          : undefined,
      },
      securityType: values.financialInstrument?.securityType ?? undefined,
      nominalValue:
        values.financialInstrument?.nominalValue ??
        DEFAULT_NOMINAL_VALUE.toString(),
    },
    operationType: orderType,
    startDate: values.startDate ?? undefined,
    transactionVolume: values.transactionVolume
      ? {
          currency: values.transactionVolume?.currency.label!,
          quantity: Number(values.transactionVolume?.quantity!),
          transactionAmount:
            values.executionTerms?.orderType?.value ===
            OrderExecutionTermsOrderType.Market
              ? undefined
              : values.transactionVolume?.transactionAmount!,
          unitPrice:
            values.executionTerms?.orderType?.value ===
            OrderExecutionTermsOrderType.Market
              ? undefined
              : values.transactionVolume?.unitPrice!,
        }
      : undefined,
    transferVolume: values.transferVolume
      ? {
          currency: values.transferVolume.currency?.label!,
          quantity: Number(values.transferVolume?.quantity!),
          transactionAmount: values.transferVolume?.transactionAmount!,
          unitPrice: values.transferVolume?.unitPrice!,
        }
      : undefined,

    recipientDetails:
      isWithdrawalOrDeposit && values.recipientDetails
        ? parseDetails(values.recipientDetails)
        : undefined,

    senderDetails:
      isWithdrawalOrDeposit && values.senderDetails
        ? parseDetails(values.senderDetails)
        : undefined,

    confirmationDocument: isWithdrawalOrDeposit
      ? {
          contractDate: values.confirmationDocument?.contractDate ?? undefined,
          contractNumber:
            values.confirmationDocument?.contractNumber ?? undefined,
          fileIds: lodash.compact(
            values.confirmationDocument?.files?.map((file) => file.id)
          ),
        }
      : undefined,

    additionalParameters:
      isWithdrawalOrDeposit && values.additionalParameters
        ? {
            settlementDate:
              values.additionalParameters.settlementDate ?? undefined,
            transactionDate:
              values.additionalParameters.transactionDate ?? undefined,
          }
        : undefined,
  };
};
